import {
  type Control,
  Controller,
  type FieldValues,
  type Path,
} from 'react-hook-form';
import { FormErrorMessage } from '@/components/form/form-error-message';
import {
  InputGroup,
  type InputGroupAddon,
  InputGroupTextarea,
} from '@/components/ui/input-group';
import { Label } from '@/components/ui/label';

type TextareaControlledProps<T extends FieldValues> = {
  control: Control<T>;
  name: Path<T>;
  label?: string;
  id: string;
  placeholder: string;
  children?: React.ReactElement<typeof InputGroupAddon>;
  ns?: string;
  onValueChange?: (value: number | string) => void;
} & React.ComponentProps<'textarea'>;

export function TextareaControlled<T extends FieldValues>({
  control,
  name,
  label,
  children,
  id,
  placeholder,
  ns = 'common',
  disabled,
  onValueChange,
  ...props
}: TextareaControlledProps<T>) {
  return (
    <div className='space-y-2'>
      {label && <Label htmlFor={name}>{label}</Label>}
      <Controller
        control={control}
        name={name}
        render={({ field, fieldState }) => (
          <>
            <InputGroup data-disabled={disabled}>
              <InputGroupTextarea
                disabled={disabled}
                id={id}
                placeholder={placeholder}
                {...field}
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(value);
                  onValueChange?.(value);
                }}
                {...props}
              />
              {children && children}
            </InputGroup>
            <FormErrorMessage error={fieldState.error?.message} ns={ns} />
          </>
        )}
      />
    </div>
  );
}
